import { useState, useMemo } from 'react';
import {
  Box,
  Paper,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { Edit, Plus, Archive } from 'lucide-react';
import { useMetricDefinitions, useArchiveMetricDefinition, usePortfolioCompanies } from '@diggens/shared-hooks';
import { useCurrentUser } from '@diggens/user-management';
import type { MetricDefinition } from '@diggens/types';
import { ICON_SIZES, ICON_STROKE } from '../../../constants/icons';
import { MetricDefinitionDrawer } from '../../../components/MetricDefinitionDrawer';

interface MetricDefinitionsTabProps {
  fundId: string;
}

export function MetricDefinitionsTab({ fundId }: MetricDefinitionsTabProps) {
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [selectedDefinition, setSelectedDefinition] = useState<MetricDefinition | null>(null);
  const { data: definitions, isLoading, error } = useMetricDefinitions(fundId);
  const { data: portfolioCompanies } = usePortfolioCompanies(fundId);
  const { data: currentUser } = useCurrentUser();
  const archiveDefinition = useArchiveMetricDefinition();

  const companyMap = useMemo(() => {
    return new Map(portfolioCompanies?.map((pc) => [pc.id, pc.name]));
  }, [portfolioCompanies]);

  const handleEdit = (definition: MetricDefinition) => {
    setSelectedDefinition(definition);
    setEditDialogOpen(true);
  };

  const handleAdd = () => {
    setSelectedDefinition(null);
    setEditDialogOpen(true);
  };

  const handleArchiveClick = (definition: MetricDefinition) => {
    setSelectedDefinition(definition);
    setArchiveDialogOpen(true);
  };

  const handleArchiveConfirm = async () => {
    if (!selectedDefinition) return;

    try {
      await archiveDefinition.mutateAsync({
        definitionId: selectedDefinition.id,
        fundId,
      });
      setArchiveDialogOpen(false);
      setSelectedDefinition(null);
    } catch (err) {
      console.error('Failed to archive metric definition:', err);
    }
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">Error loading metric definitions: {error instanceof Error ? error.message : 'Unknown error'}</Alert>;
  }

  // Group metrics: fund-level (required), fund-level (optional), company-specific
  const groupedDefinitions = useMemo(() => {
    const defs = definitions || [];
    const fundLevelRequired = defs.filter((d) => !d.portfolio_company_id && d.is_required);
    const fundLevelOptional = defs.filter((d) => !d.portfolio_company_id && !d.is_required);
    const companySpecific = defs.filter((d) => d.portfolio_company_id);

    // Sort company-specific by company name, then metric name
    companySpecific.sort((a, b) => {
      const companyA = companyMap.get(a.portfolio_company_id!) || '';
      const companyB = companyMap.get(b.portfolio_company_id!) || '';
      if (companyA !== companyB) return companyA.localeCompare(companyB);
      return a.name.localeCompare(b.name);
    });

    return { fundLevelRequired, fundLevelOptional, companySpecific };
  }, [definitions, companyMap]);

  const fundLevelMetrics = [...groupedDefinitions.fundLevelRequired, ...groupedDefinitions.fundLevelOptional];
  const companySpecificMetrics = groupedDefinitions.companySpecific;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Metric Definitions ({fundLevelMetrics.length + companySpecificMetrics.length})</Typography>
        <Button size="small" variant="contained" startIcon={<Plus size={ICON_SIZES.sm} strokeWidth={ICON_STROKE} />} onClick={handleAdd} sx={{ fontSize: '0.75rem', textTransform: 'none', py: 0.5 }}>
          Add Metric
        </Button>
      </Box>

      {/* Fund-Level Metrics Table */}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: '20%' }}>Metric Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Data Type</TableCell>
              <TableCell align="center">Required</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {fundLevelMetrics.length > 0 ? (
              fundLevelMetrics.map((definition, index) => {
                const isFirstOptional = !definition.is_required && (index === 0 || fundLevelMetrics[index - 1]?.is_required);

                return (
                  <>
                    {isFirstOptional && (
                      <TableRow key={`optional-header-${definition.id}`}>
                        <TableCell
                          colSpan={5}
                          sx={{
                            bgcolor: 'action.hover',
                            borderTop: '2px solid',
                            borderBottom: '2px solid',
                            borderColor: 'divider',
                            py: 0.5,
                          }}
                        >
                          <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                            Optional Metrics
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                    <TableRow key={definition.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {definition.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{definition.description || '-'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{definition.data_type.charAt(0).toUpperCase() + definition.data_type.slice(1)}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2">{definition.is_required ? 'Yes' : 'No'}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                          <IconButton size="small" onClick={() => handleEdit(definition)}>
                            <Edit size={ICON_SIZES.sm} strokeWidth={ICON_STROKE} />
                          </IconButton>
                          <IconButton size="small" onClick={() => handleArchiveClick(definition)} color="warning">
                            <Archive size={ICON_SIZES.sm} strokeWidth={ICON_STROKE} />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  </>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No metric definitions found
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Company-Specific Metrics Table */}
      {companySpecificMetrics.length > 0 && (
        <TableContainer component={Paper} variant="outlined" sx={{ mt: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell
                  colSpan={6}
                  sx={{
                    bgcolor: 'action.hover',
                    py: 1,
                  }}
                >
                  <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                    Company-Specific Metrics
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ width: '20%' }}>Metric Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Data Type</TableCell>
                <TableCell align="center">Required</TableCell>
                <TableCell sx={{ width: '15%' }}>Portfolio Company</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {companySpecificMetrics.map((definition) => (
                <TableRow key={definition.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {definition.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{definition.description || '-'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{definition.data_type.charAt(0).toUpperCase() + definition.data_type.slice(1)}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2">{definition.is_required ? 'Yes' : 'No'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{companyMap.get(definition.portfolio_company_id!) || 'Unknown'}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                      <IconButton size="small" onClick={() => handleEdit(definition)}>
                        <Edit size={ICON_SIZES.sm} strokeWidth={ICON_STROKE} />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleArchiveClick(definition)} color="warning">
                        <Archive size={ICON_SIZES.sm} strokeWidth={ICON_STROKE} />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {currentUser && <MetricDefinitionDrawer open={editDialogOpen} onClose={() => setEditDialogOpen(false)} definition={selectedDefinition} fundId={fundId} tenantId={currentUser.tenant_id!} />}

      <Dialog open={archiveDialogOpen} onClose={() => setArchiveDialogOpen(false)}>
        <DialogTitle>Archive Metric Definition</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to archive "{selectedDefinition?.name}"?
            <br />
            <br />
            This will hide it from new submissions, but existing metrics will remain intact.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setArchiveDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleArchiveConfirm} color="warning" variant="contained" disabled={archiveDefinition.isPending}>
            {archiveDefinition.isPending ? 'Archiving...' : 'Archive'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
